import { Alert, Button, Code, Group, Radio, Stack, Stepper, Table, Text, Textarea } from "@mantine/core";
import { Dropzone, MIME_TYPES } from "@mantine/dropzone";
import { IconArrowLeft, IconArrowRight, IconFileDescription, IconInfoCircle, IconUpload, IconX } from "@tabler/icons-react";
import { useState } from "react";
import { gqlx } from "shared/api/gqlx";
import { UsersSearchRepository } from "shared/components/organization/users/data";
import { runAsync } from "shared/utils/fns";
import { logger } from "shared/utils/logger";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";
import { container } from "tsyringe";
import z from "zod";

const usersRepo = container.resolve(UsersSearchRepository);

function parseCsv(raw: string) {
    const parsed = raw
        .trim()
        .split("\n")
        .map((row) => row.replaceAll("\t", ",").split(","))
        .map(([name, email]) => {
            const parsedEmail = z.string().email().safeParse(email?.trim()).data?.toLowerCase();
            return !!parsedEmail ? { name: name?.trim(), email: parsedEmail } : undefined;
        })
        .filter((it) => !!it);

    return Array.from(new Set(parsed));
}

const BATCH_SIZE = 20 as const;

async function batchInviteUsers(userList: { name: string; email: string }[]) {
    const results = [];
    for (let x = 0; x < userList.length; x += BATCH_SIZE) {
        try {
            const batch = userList.slice(x, x + BATCH_SIZE);
            const res = await gqlx(
                graphql(`
                    mutation BulkUserInvite($users: [ImportUserInput!]!) {
                        importUsers(data: { autoVerifyEuds: false, importAsOptInUser: false, users: $users }) {
                            userCount
                            userCreationErrors {
                                error
                                name
                            }
                        }
                    }
                `),
                { users: batch.map((user) => ({ loginEmail: user.email, name: user.name, groups: [], euds: [] })) },
            );
            results.push(res);
        } catch (e) {
            logger.error(e);
        }
    }

    return {
        successCount: results.reduce((acc, next) => next.importUsers.userCount + acc, 0),
        errors: results.flatMap((it) => it.importUsers.userCreationErrors),
    };
}

export const UploadUsersForm = (props: { onClose: () => unknown }) => {
    const [step, setStep] = useState(0);
    const [mode, setMode] = useState<"text" | "file">("file");
    const [textInputState, setTextInputState] = useState("");
    const [filename, setFilename] = useState<string>();
    const [userList, setUserList] = useState<{ name: string; email: string }[]>([]);
    const [invitePending, setInvitePending] = useState(false);
    const [inviteResults, setInviteResults] = useState<{
        successCount: number;
        errors: {
            error: string;
            name: string;
        }[];
    }>();

    return (
        <Stepper active={step} allowNextStepsSelect={false} size="xs">
            <Stepper.Step label="Upload User List">
                <Stack>
                    <Text fz="sm">Accepts a list of names & emails in CSV format</Text>
                    <Radio.Group defaultValue={mode} onChange={(val) => setMode(val as "text" | "file")}>
                        <Group>
                            <Radio value="file" label="File" />
                            <Radio value="text" label="Text" />
                        </Group>
                    </Radio.Group>
                    {mode == "file" ? (
                        <Dropzone
                            onDrop={(files) => {
                                const reader = new FileReader();
                                reader.onload = (e) => {
                                    const res = e?.target?.result?.toString();
                                    if (!res) return notify.show.error({ message: "Error loading file data" });
                                    try {
                                        setUserList(parseCsv(res));
                                        setFilename(files[0].name);
                                    } catch (e) {
                                        notify.show.error({ message: "Error loading email data: " + (e as string) });
                                    }
                                };

                                reader.readAsText(files[0]);
                            }}
                            multiple={false}
                            accept={[MIME_TYPES.csv]}
                        >
                            <Group justify="center" gap="xl" style={{ pointerEvents: "none" }}>
                                <Stack align="center">
                                    <Dropzone.Accept>
                                        <IconUpload size={32} />
                                    </Dropzone.Accept>
                                    <Dropzone.Reject>
                                        <IconX size={32} />
                                    </Dropzone.Reject>
                                    <Dropzone.Idle>
                                        <IconFileDescription size={52} />
                                    </Dropzone.Idle>

                                    <Text size="md" fw={600} inline>
                                        {!filename ? "Upload User List" : filename}
                                    </Text>
                                    <Text size="xs" c="dimmed" inline>
                                        .csv
                                    </Text>
                                </Stack>
                            </Group>
                        </Dropzone>
                    ) : (
                        <Textarea value={textInputState} onChange={(e) => setTextInputState(e.target.value)} />
                    )}
                    <Text>Example:</Text>
                    <Code style={{ whiteSpace: "pre-wrap" }}>
                        <pre>{`John Doe, johndoe@gmail.com\nJane Doe, janedoe@gmail.com\nJohn Smith, johnsmith@gmail.com`}</pre>
                    </Code>
                    <Group justify="flex-end" mt="xl">
                        <Button
                            disabled={mode == "file" ? filename == undefined : textInputState.length == 0}
                            onClick={() => {
                                if (mode == "text") setUserList(parseCsv(textInputState));
                                setStep(step + 1);
                            }}
                            rightSection={<IconArrowRight size={16} />}
                        >
                            Next
                        </Button>
                    </Group>
                </Stack>
            </Stepper.Step>
            <Stepper.Step label="Review & Confirm">
                <Stack>
                    <Text>
                        Users found: <strong>{userList.length}</strong>
                    </Text>
                    <Table.ScrollContainer minWidth={200} maxHeight={600}>
                        <Table striped withTableBorder withRowBorders>
                            <Table.Tbody>
                                {userList.map((user) => (
                                    <Table.Tr key={user.email}>
                                        <Table.Td>{user.name}</Table.Td>
                                        <Table.Td>{user.email}</Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>
                    <Alert color="red" icon={<IconInfoCircle size={16} />} title="Inviting users">
                        The users in this list will receive an invitation email with instructions on how to activate and manage their
                        accounts. Double check the emails provided are correct.
                    </Alert>
                    <Group justify="space-between">
                        <Button leftSection={<IconArrowLeft size={16} />} variant="default" onClick={() => setStep(step - 1)}>
                            Back
                        </Button>
                        <Button
                            loading={invitePending}
                            onClick={() => {
                                runAsync(async () => {
                                    setInvitePending(true);
                                    const invitedUsers = await batchInviteUsers(userList);
                                    usersRepo.refresh();
                                    setInvitePending(false);
                                    setInviteResults(invitedUsers);
                                    setStep(step + 1);
                                });
                            }}
                        >
                            Invite Users
                        </Button>
                    </Group>
                </Stack>
            </Stepper.Step>
            <Stepper.Completed>
                <Stack>
                    <Text fw={600}>Results:</Text>
                    <Text>
                        Invited: <strong>{inviteResults?.successCount ?? 0}</strong>
                    </Text>
                    <Text>
                        Failed: <strong>{userList.length - (inviteResults?.successCount ?? 0)}</strong>
                    </Text>
                    {!!inviteResults && (inviteResults?.errors.length ?? 0) > 0 && (
                        <Table.ScrollContainer minWidth={200} maxHeight={300}>
                            <Table striped withTableBorder withRowBorders>
                                <Table.Tbody>
                                    {inviteResults.errors.map((user, idx) => (
                                        <Table.Tr key={user.name + idx}>
                                            <Table.Td>{user.name}</Table.Td>
                                            <Table.Td>{user.error}</Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                        </Table.ScrollContainer>
                    )}
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => props.onClose()}>
                            Close
                        </Button>
                    </Group>
                </Stack>
            </Stepper.Completed>
        </Stepper>
    );
};
